// THE GATE VOCABULARY, CODE-SIDE (lifecycle D4 + §G).
//
// Two things live here, and neither may import React or the API layer — the exhaustiveness test
// imports this module directly:
//
//   1. COND_ROUTE — for every automatic condition on every gate, where the operator goes to
//      clear it. This is the tab's own knowledge (which condition routes where); every VALUE is
//      a `ROUTES.*` call from the shared route contract, never a hand-built hash.
//
//   2. The condition LABEL constants. They mirror `utils/gates.ts` — `DEFAULT_MANUAL_ITEMS` and
//      the `cond()` literals. They exist because a gate console whose engine is down must still
//      be able to say WHAT the gate asks; only the values are missing. A drift between this
//      mirror and the engine degrades to "no route offered", never to a crash.

import { ROUTES, type Dest } from '../../routes.js';
import type { Gate } from '../../api/crm.js';

export const GATE_ORDER: Gate[] = ['G0', 'G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7', 'G8'];

/**
 * Mirrors the `cond('…')` literals in `src/server/utils/gates.ts`, gate by gate, in order.
 * 27 automatic conditions.
 */
export const AUTO_CONDITION_LABELS: Record<Gate, string[]> = {
  G0: [
    'Client record exists',
    'Contact on file and named for this engagement',
    'Qualification note recorded for this engagement',
  ],
  G1: [
    'At least one document ingested and ready',
    'Zero pending proposals',
    'Zero unresolved conflicts',
    'Every requirement confirmed',
  ],
  G2: [
    'Every MUST requirement maps to a deliverable',
    'No deliverable without a requirement',
    'Estimate present',
  ],
  G3: [
    'A SOW version is signed',
    'Signature record captured on the signed version',
    'Baseline created',
  ],
  G4: [
    'Kickoff date logged',
    'Plan has milestones',
    'Client access provisioned',
  ],
  G5: [
    'All deliverables delivered',
    'Every deliverable sits on an acceptance record',
    'At least one acceptance record issued',
  ],
  G6: [
    'No open blocking or major defects',
    'Acceptance record signed or deemed',
    'Signature record captured on an accepted record',
  ],
  G7: [
    'All defects closed',
    'No open questions outstanding',
  ],
  G8: [
    'No undecided change requests',
    'No unsigned acceptance records',
    'No open operator tasks',
  ],
};

/** Mirrors `DEFAULT_MANUAL_ITEMS`. Shown, unticked, when the engine cannot be reached. */
export const MANUAL_CONDITION_LABELS: Record<Gate, string[]> = {
  G0: ['Sponsor identified and reachable', 'Commercial model agreed in principle', 'Capacity available for the window'],
  G1: ['Discovery sessions complete', 'Register walkthrough held with the client'],
  G2: ['Estimate sanity-checked against capacity', 'Delivery approach agreed internally'],
  G3: ['Counter-signed copy filed', 'Payment terms and PO confirmed'],
  G4: ['Kickoff held with the client team', 'Environments and access provisioned', 'Reporting cadence agreed'],
  G5: ['Milestone demo delivered', 'Milestone invoice raised'],
  G6: ['UAT results reviewed with the sponsor', 'Residual minor defects agreed in writing'],
  G7: ['Runbook delivered', 'Training / handover session logged', 'Hypercare window agreed'],
  G8: ['Closure note written', 'Lessons learned captured', 'Final invoice raised'],
};

/**
 * Where a condition is cleared.
 *
 * `dest` is null for the conditions whose subject IS this page or this engagement's own record —
 * a route that lands back where you already are is worse than no route, so the row says what to
 * do instead (`self`).
 */
export interface CondRoute {
  /** The control's words. States the destination AND the filter it arrives with. */
  label: string;
  /** `null` ⇒ the work happens here; `self` names which in-page thing to open. */
  dest: ((ctx: RouteCtx) => Dest) | null;
  self?: 'record' | 'gate';
}

export interface RouteCtx {
  engagementId: number;
  clientId: number;
}

export const COND_ROUTE: Record<Gate, Record<string, CondRoute>> = {
  G0: {
    'Client record exists': {
      label: 'Client record',
      dest: (c) => ROUTES.client({ id: c.clientId }),
    },
    'Contact on file and named for this engagement': {
      label: 'Name the sponsor on the engagement record',
      dest: null,
      self: 'record',
    },
    'Qualification note recorded for this engagement': {
      label: 'Write the note on a G0 condition below',
      dest: null,
      self: 'gate',
    },
  },
  G1: {
    'At least one document ingested and ready': {
      label: 'Documents · not ready',
      dest: (c) => ROUTES.documents({ engagement: c.engagementId, status: 'not-ready' }),
    },
    'Zero pending proposals': {
      label: 'Proposals · pending',
      dest: (c) => ROUTES.proposals({ engagement: c.engagementId, status: 'pending' }),
    },
    'Zero unresolved conflicts': {
      label: 'Proposals · pending conflicts',
      dest: (c) => ROUTES.proposals({ engagement: c.engagementId, status: 'pending' }),
    },
    'Every requirement confirmed': {
      label: 'Register · status=draft',
      dest: (c) => ROUTES.register({ engagement: c.engagementId, status: 'draft' }),
    },
  },
  G2: {
    'Every MUST requirement maps to a deliverable': {
      label: 'Register · unfunded MUST',
      dest: (c) => ROUTES.register({ engagement: c.engagementId, priority: 'must', gap: 'coverage' }),
    },
    'No deliverable without a requirement': {
      label: 'Register · deliverables',
      dest: (c) => ROUTES.register({ engagement: c.engagementId, tab: 'deliverables' }),
    },
    'Estimate present': {
      label: 'Contracting · plan',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId, tab: 'plan' }),
    },
  },
  G3: {
    'A SOW version is signed': {
      label: 'Contracting · SOW',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId }),
    },
    'Signature record captured on the signed version': {
      label: 'Contracting · SOW',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId }),
    },
    'Baseline created': {
      label: 'Contracting · baselines',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId, tab: 'baselines' }),
    },
  },
  G4: {
    'Kickoff date logged': {
      label: 'Set the kickoff date on the engagement record',
      dest: null,
      self: 'record',
    },
    'Plan has milestones': {
      label: 'Contracting · plan',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId, tab: 'plan' }),
    },
    'Client access provisioned': {
      label: 'Client Portal · grants',
      dest: (c) => ROUTES.portalAdmin({ engagement: c.engagementId }),
    },
  },
  G5: {
    'All deliverables delivered': {
      label: 'Register · deliverables',
      dest: (c) => ROUTES.register({ engagement: c.engagementId, tab: 'deliverables' }),
    },
    'Every deliverable sits on an acceptance record': {
      label: 'Acceptance',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId }),
    },
    'At least one acceptance record issued': {
      label: 'Acceptance',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId }),
    },
  },
  G6: {
    'No open blocking or major defects': {
      label: 'Acceptance · defects',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId, tab: 'defects' }),
    },
    'Acceptance record signed or deemed': {
      label: 'Acceptance',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId }),
    },
    'Signature record captured on an accepted record': {
      label: 'Acceptance',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId }),
    },
  },
  G7: {
    'All defects closed': {
      label: 'Acceptance · defects',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId, tab: 'defects' }),
    },
    'No open questions outstanding': {
      label: 'Register · open questions',
      dest: (c) => ROUTES.register({ engagement: c.engagementId, tab: 'questions', status: 'open' }),
    },
  },
  G8: {
    'No undecided change requests': {
      label: 'Contracting · change requests',
      dest: (c) => ROUTES.contracting({ engagement: c.engagementId, tab: 'changes' }),
    },
    'No unsigned acceptance records': {
      label: 'Acceptance',
      dest: (c) => ROUTES.acceptance({ engagement: c.engagementId }),
    },
    'No open operator tasks': {
      label: 'Dashboard · open tasks',
      dest: () => ROUTES.dashboard({ tasks: 'open' }),
    },
  },
};

/** Unknown labels resolve to `undefined` — the row then shows no control rather than crashing. */
export const routeFor = (gate: Gate, label: string): CondRoute | undefined =>
  COND_ROUTE[gate]?.[label];
